import "core-js/modules/es6.regexp.replace.js";
import "core-js/modules/es6.regexp.split.js";
import "core-js/modules/es6.promise.js";
import "core-js/modules/es6.object.to-string.js";
// # Cookies
//
// Get http cookies from various different sources: browser environment, node
// server request object, etc.
//
// ## Cookie Context
//
// Calls to get cookies should provide context so the appropriate set of
// cookies can be returned. Cookies are inherently dependent on context: url
// (domain and path), https-or-http ("secure"), client-or-server ("http
// only"), expiration, etc.
//
// ## API
//
// getCookies(context) => promise
//
// Get cookies given a context. Context should include the following options:
//
// * context.url: The url to get cookies for
// * context.config: The config object passed to the NwApiSDK factory (as it
//   may contain custom cookie logic in config.getCookies)
// * context.serverRequest: A server request object
//
// ## Implementation
//
// The `COOKIE_SOURCES` array provides an ordered list of sources to get
// cookies given a context. Each source is used until one returns a set of
// cookies.
//
// ### Source Functions
//
// Each source is a function that takes a context and returns an object
// containing cookies. A source can also return null, indicating the source is
// not available for the given context and the next source should be used. For
// example, the "Node.js server request" source function returns null when
// context.serverRequest is not present.
//
// Source functions can also return a promise.
//
// ### Cookies Object
//
// Source functions return objects containing cookies for the given context:
//
// ```js
// { csrf_token: '...' }
// ```
//
// ## Rationale
//
// The primary motivation for this abstraction is to allow an application
// running in a React Native environment to provide cookies from the
// underlying iOS and Android systems.
//
// ## Future Refactor
//
// It may be worth considering removing built-in environment-specific logic
// and relying solely on `config.getCookies` being given.
//
// Source functions are called in order until one returns a non-null value.
var COOKIE_SOURCES = [function getFromConfig(context) {
  if (context.config && context.config.getCookies) {
    return context.config.getCookies(context);
  }
}, function getFromServerRequest(context) {
  if (context.serverRequest) {
    return context.serverRequest.cookies || {};
  }
}, // eslint-disable-next-line no-unused-vars
function getFromBrowserCookies(context) {
  // Safe access to window.document.cookie if it's available.
  // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef -- directive added automatically by Shepherd migration
  if (typeof window !== 'undefined' && window.document) {
    // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef -- directive added automatically by Shepherd migration
    return getCookiesFromString(window.document.cookie);
  }
}]; // Call each function in COOKIE_SOURCES until one returns something

function getCookies(context) {
  context = context || {};
  var val;

  for (var i = 0; i < COOKIE_SOURCES.length; i += 1) {
    val = COOKIE_SOURCES[i](context);

    if (val) {
      return Promise.resolve(val).then(function (val) {
        // If the promise resolves to a falsy value assume no cookies (and not
        // an indication to use the approach)
        if (!val) {
          return {};
        }

        return val;
      });
    }
  } // No cookies available


  return Promise.resolve({});
} // Get a cookies object from the string found in document.cookie (also the
// same as `Cookie` header strings).
// Adapted from http://stackoverflow.com/questions/10730362/get-cookie-by-name


function getCookiesFromString(cookiesString) {
  return cookiesString.split(';').reduce(function (acc, cookieString) {
    var parts = cookieString.split('=');

    if (parts.length > 0) {
      acc[trim(parts[0])] = trim(parts.slice(1).join(''));
    }

    return acc;
  }, {});
} // Trim whitespace from both sides of a string


function trim(str) {
  return str.replace(/^\s+|\s+$/g, '');
}

export { getCookies, getCookiesFromString };